import jsPDF from 'jspdf';

/**
 * Blank, printable "Part Form" — the same fields the web New-Part form
 * collects, laid out as a paper form a plant-side person can fill out
 * by hand and hand back to a director. Handed back as a jsPDF doc so
 * savePartFormPdf() (or any other caller) can .save() / getBlob() it.
 *
 * Matches the visual style of plantPdf.js / proposalPdf.js — orange bar
 * + Proxemics logo header, orange section headings, helvetica throughout —
 * so all Portal-generated PDFs feel like they come from the same place.
 *
 * Every field is drawn as a write-in BOX (labeled rectangle) rather than
 * an underline, so the form reads as a "fill these in" sheet. Structure
 * mirrors the web form (Jillian, Aug 2026): an OEM Plant Info section
 * (Part Name, Supplier, Plant, Vehicle) followed by Part/s Info — four
 * blank, numbered part cards (Part 01–Part 04), each with its own Part #,
 * Description, plant-side locations, and Notes.
 */

// Kept in sync with --brand-primary in theme.css. Duplicated (rather than
// imported from plantPdf) so each PDF utility stays self-contained, which
// is the convention already established in plantPdf.js / proposalPdf.js.
const BRAND = [232, 119, 34]; // #E87722
const INK = [17, 24, 39]; // #111827
const MUTED = [100, 116, 139]; // #64748b
const DIVIDER = [229, 231, 235]; // #e5e7eb

const MARGIN_X = 48; // ~0.67"
const PAGE_TOP = 48;

const LOGO_URL = '/Proxemics-Logo.png';

// Cached logo loader, same pattern as plantPdf.js — repeat clicks on the
// "Part Form" button don't refetch the PNG.
let logoPromise = null;
function loadLogo() {
  if (logoPromise) return logoPromise;
  logoPromise = new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve({
        dataUrl: canvas.toDataURL('image/png'),
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.onerror = (err) => {
      logoPromise = null;
      reject(err);
    };
    img.src = LOGO_URL;
  });
  return logoPromise;
}

// Tiny Y-cursor layout engine. Same shape as makeLayout() in plantPdf.js —
// sections emit content, the engine auto-paginates when it runs out of room.
function makeLayout(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const usableWidth = pageWidth - MARGIN_X * 2;
  const state = { y: PAGE_TOP };

  const ensureRoom = (needed) => {
    if (state.y + needed > pageHeight - 48) {
      doc.addPage();
      state.y = PAGE_TOP;
    }
  };

  return {
    pageWidth,
    pageHeight,
    usableWidth,
    get y() {
      return state.y;
    },
    advance(n) {
      state.y += n;
    },
    moveTo(n) {
      state.y = n;
    },
    ensureRoom,
  };
}

function drawHeader(doc, layout, logo) {
  // Orange brand bar.
  doc.setFillColor(...BRAND);
  doc.rect(0, 0, layout.pageWidth, 6, 'F');

  // Logo at top-left at a fixed height; width is derived so the logo never
  // squishes. Falls through to a text-only header if the asset fails to load.
  let titleY = 34;
  if (logo) {
    const logoTop = 34;
    const logoHeight = 45;
    const logoWidth = (logo.width / logo.height) * logoHeight;
    doc.addImage(logo.dataUrl, 'PNG', MARGIN_X, logoTop, logoWidth, logoHeight);
    titleY = logoTop + logoHeight + 36;
  }

  // Form title, left aligned.
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...INK);
  doc.text('Part Form', MARGIN_X, titleY);

  // Right-aligned instructional subtitle — cues the reader that this is
  // a paper form to be completed and handed back.
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text(
    'Fill out the form and submit it to your manager.',
    layout.pageWidth - MARGIN_X,
    titleY,
    { align: 'right' },
  );

  layout.moveTo(titleY + 24);
}

function sectionHeading(doc, layout, title) {
  layout.ensureRoom(30);
  layout.advance(8);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...BRAND);
  doc.text(title.toUpperCase(), MARGIN_X, layout.y);
  layout.advance(4);
  doc.setDrawColor(...DIVIDER);
  doc.setLineWidth(0.5);
  doc.line(MARGIN_X, layout.y, layout.pageWidth - MARGIN_X, layout.y);
  layout.advance(14);
}

/**
 * Render a write-in field as a boxed rectangle with a small muted label
 * sitting on top of it:
 *
 *   PART NAME *
 *   ┌──────────────────────────────────────────────────────────┐
 *   │                                                          │
 *   └──────────────────────────────────────────────────────────┘
 *
 * `height` is the box's interior height; pass more for multi-line
 * write-in fields like Part Description and Notes. `width` / `x` let
 * callers lay fields out side-by-side.
 *
 * Layout budget per call (all added to `layout.y`):
 *   LABEL_ABOVE   room for label ascenders that extend up from baseline
 *   LABEL_BELOW   gap between label baseline and top of the box
 *   height        the box's interior
 *   gapBelow      breathing room before the next field
 */
function fieldBox(doc, layout, label, opts = {}) {
  const {
    width = layout.usableWidth,
    x = MARGIN_X,
    height = 28,
    gapBelow = 14,
    labelSize = 8,
    hint, // optional placeholder-style text to print INSIDE the box
  } = opts;
  const LABEL_ABOVE = 9; // label ascender room above baseline
  const LABEL_BELOW = 4; // gap between baseline and box top
  const totalH = LABEL_ABOVE + LABEL_BELOW + height + gapBelow;
  layout.ensureRoom(totalH);

  // Reserve ascender space above the baseline so the label can't overlap
  // whatever sits above this field.
  layout.advance(LABEL_ABOVE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(labelSize);
  doc.setTextColor(...MUTED);
  doc.text((label || '').toUpperCase(), x, layout.y);

  // Box below the baseline. Light gray stroke so the boxes recede and
  // the handwritten answers pop.
  const boxTop = layout.y + LABEL_BELOW;
  doc.setDrawColor(...DIVIDER);
  doc.setLineWidth(0.75);
  doc.roundedRect(x, boxTop, width, height, 3, 3);

  // Optional hint text rendered INSIDE the box — italic + muted so it
  // reads like an HTML placeholder. Used for fields where it's worth
  // telling the person filling in the form how to interpret the space
  // (e.g. "If multiple, fill out additional fields below" on Part #).
  if (hint) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    // Baseline-anchored text, placed roughly one line-height below the
    // box's top edge so it sits comfortably inside rather than straddling
    // the border.
    doc.text(hint, x + 8, boxTop + 13);
  }

  layout.advance(LABEL_BELOW + height + gapBelow);
}

/**
 * Draw a bordered, numbered part card ("PART 01", "PART 02", …) matching
 * the web form's Part/s Info cards. Each holds the seven fields a plant-
 * side person fills in per part / part type (Bright White / Driver Side /
 * etc.):
 *
 *   header: PART 0N
 *   row 1: Part Type | Part #                     (two columns)
 *   row 2: Part Description                        (full width)
 *   row 3: Sequenced | Kitting / Storage          (two columns)
 *   row 4: Installed (Assembly Location)          (full width)
 *   row 5: Notes                                  (full width)
 *
 * `index` is the 1-based card number, printed in the header so a filled
 * form can be referenced as "Part 01, Part 02…" just like the web form.
 */
function drawVariantCard(doc, layout, index) {
  // Compact tuning (Jillian, Aug 2026): smaller write-in boxes + tighter
  // gaps + Installed and Notes sharing a row, so two cards fit on page 1
  // (below the intro section) and three on page 2 — saves paper.
  const CARD_PAD = 10;
  // Two write-in heights: a modest one for the short top row (Part Type /
  // Part #) and a taller one for the fields people actually write into —
  // Description, the location fields, and Notes (Jillian, Aug 2026: fill
  // the empty space, but still keep 2 cards on page 1 / 3 on page 2).
  const H_TOP = 18;
  const H_BIG = 26;
  const ROW_GAP = 8; // gapBelow between rows inside the card
  const HEADER_H = 16; // room for the "PART/VARIANT 0N" card header
  // Per-row block height = LABEL_ABOVE(9) + LABEL_BELOW(4) + box + gapBelow.
  const BLOCK = (h, gap) => 9 + 4 + h + gap;
  // Four vertical rows; the last row uses gapBelow:0 (card padding governs).
  //   1. Part Type | Part #                   (split, short)
  //   2. Part Description                     (full,  tall)
  //   3. Sequenced | Kitting / Storage        (split, tall)
  //   4. Installed | Notes                    (split, tall)
  const cardH =
    CARD_PAD * 2 +
    HEADER_H +
    BLOCK(H_TOP, ROW_GAP) +
    BLOCK(H_BIG, ROW_GAP) +
    BLOCK(H_BIG, ROW_GAP) +
    BLOCK(H_BIG, 0);
  layout.ensureRoom(cardH + 12);

  const cardTop = layout.y;
  const cardLeft = MARGIN_X;
  const cardWidth = layout.usableWidth;

  // Outer border.
  doc.setDrawColor(...DIVIDER);
  doc.setLineWidth(0.75);
  doc.roundedRect(cardLeft, cardTop, cardWidth, cardH, 4, 4);

  const innerX = cardLeft + CARD_PAD;
  const innerWidth = cardWidth - CARD_PAD * 2;
  const colGap = 18;
  const halfWidth = (innerWidth - colGap) / 2;

  // Card header — "PART 01", "PART 02", … so the paper cards are numbered
  // like the web form's.
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text(
    `PART/VARIANT ${String(index).padStart(2, '0')}`,
    innerX,
    cardTop + CARD_PAD + 6,
  );

  // First field row starts below the header.
  layout.moveTo(cardTop + CARD_PAD + HEADER_H);

  // Row 1: Part Type + Part # side by side. Render the first, rewind the
  // cursor, render the second — a little trick so both sit on the same Y.
  // Every row passes ROW_GAP explicitly so each row's advance matches the
  // cardH math above (fieldBox's default gapBelow is bigger).
  const rowTop = layout.y;
  fieldBox(doc, layout, 'Part Type', {
    x: innerX,
    width: halfWidth,
    height: H_TOP,
    gapBelow: ROW_GAP,
  });
  const afterFirstRow = layout.y;
  layout.moveTo(rowTop);
  fieldBox(doc, layout, 'Part #', {
    x: innerX + halfWidth + colGap,
    width: halfWidth,
    height: H_TOP,
    gapBelow: ROW_GAP,
  });
  layout.moveTo(afterFirstRow);

  // Row 2: Part Description (full width).
  fieldBox(doc, layout, 'Part Description', {
    x: innerX,
    width: innerWidth,
    height: H_BIG,
    gapBelow: ROW_GAP,
  });

  // Row 3: Sequenced + Kitting share a row — same split-row trick as
  // row 1 so the card stays compact.
  const row3Top = layout.y;
  fieldBox(doc, layout, 'Sequenced (Sequencing Location)', {
    x: innerX,
    width: halfWidth,
    height: H_BIG,
    gapBelow: ROW_GAP,
  });
  const afterRow3 = layout.y;
  layout.moveTo(row3Top);
  fieldBox(doc, layout, 'Kitting / Storage (Warehouse Location)', {
    x: innerX + halfWidth + colGap,
    width: halfWidth,
    height: H_BIG,
    gapBelow: ROW_GAP,
  });
  layout.moveTo(afterRow3);

  // Row 4: Installed + Notes share a row — same split-row trick — so the
  // card stays compact. Last row uses gapBelow:0 so the card's bottom
  // padding governs spacing instead.
  const row4Top = layout.y;
  fieldBox(doc, layout, 'Installed (Assembly Location)', {
    x: innerX,
    width: halfWidth,
    height: H_BIG,
    gapBelow: 0,
  });
  const afterRow4 = layout.y;
  layout.moveTo(row4Top);
  fieldBox(doc, layout, 'Notes', {
    x: innerX + halfWidth + colGap,
    width: halfWidth,
    height: H_BIG,
    gapBelow: 0,
  });
  layout.moveTo(afterRow4);

  // Drop the cursor past the card plus some breathing room.
  layout.moveTo(cardTop + cardH + 14);
}

/**
 * Running footer on every page — "Proxemics Consulting / Part Form" on
 * the left and the page number on the right. Matches the footer on
 * proposalPdf.js so the Portal-generated PDFs all share the same chrome.
 * Drawn after the content is laid out so every page gets the footer,
 * including the ones the layout engine auto-added mid-build.
 */
function drawFooter(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    const footerY = doc.internal.pageSize.getHeight() - 28;
    doc.text('Proxemics Consulting / Part Form', MARGIN_X, footerY);
    doc.text(
      String(i),
      doc.internal.pageSize.getWidth() - MARGIN_X,
      footerY,
      { align: 'right' },
    );
  }
}

/**
 * Build the blank part-form PDF. Exported separately from savePartFormPdf
 * so callers can preview / attach the doc without triggering a download.
 */
export async function buildPartFormPdf() {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const layout = makeLayout(doc);

  let logo = null;
  try {
    logo = await loadLogo();
  } catch (err) {
    // Non-fatal — PDF still generates with a text-only header.
    // eslint-disable-next-line no-console
    console.warn('[partFormPdf] logo load failed, continuing without it', err);
  }

  drawHeader(doc, layout, logo);

  // --- OEM Plant Info ------------------------------------------------
  // Matches the web form's top section: the part's name plus the OEM-side
  // context (supplier, plant, vehicle). Part # and Part Description are NO
  // longer here — they live inside each part card below (Jillian, Aug 2026).
  sectionHeading(doc, layout, 'OEM Plant Info');
  const half = (layout.usableWidth - 18) / 2;
  // Part Name + Supplier on one row, Plant + Vehicle on the next — paired
  // to keep the intro section short so two part cards still fit on page 1.
  // These four boxes are double the default height (Jillian, Aug 2026).
  const OEM_BOX_H = 56;
  const nameRowTop = layout.y;
  fieldBox(doc, layout, 'Part Name', { width: half, height: OEM_BOX_H, gapBelow: 0 });
  const afterNameRow = layout.y;
  layout.moveTo(nameRowTop);
  fieldBox(doc, layout, 'Supplier', {
    x: MARGIN_X + half + 18,
    width: half,
    height: OEM_BOX_H,
  });
  layout.moveTo(afterNameRow);

  const rowTop = layout.y;
  fieldBox(doc, layout, 'Plant', { width: half, height: OEM_BOX_H, gapBelow: 0 });
  const afterPlantRow = layout.y;
  layout.moveTo(rowTop);
  fieldBox(doc, layout, 'Vehicle', {
    x: MARGIN_X + half + 18,
    width: half,
    height: OEM_BOX_H,
  });
  layout.moveTo(afterPlantRow + 12);

  // --- Part/s Info ---------------------------------------------------
  // Five blank, numbered part cards (Part 01–Part 05) — one card per part
  // type (colors / sides / etc.), each carrying its own Part #, Description,
  // plant-side locations, and Notes. Compact sizing fits two on page 1 and
  // three on page 2 to save paper.
  sectionHeading(doc, layout, 'Part/s Info');
  for (let i = 0; i < 5; i += 1) {
    drawVariantCard(doc, layout, i + 1);
  }

  // Footer has to run last — it iterates every page that already exists,
  // so we can only call it after all the auto-pagination is done.
  drawFooter(doc);

  return doc;
}

/**
 * Generate and trigger a browser download. Called from the Parts page's
 * "Part Form" button. Filename follows the existing Portal PDF
 * convention (ProxemicsConsulting-{Kind}-{Identifier}.pdf).
 */
export async function savePartFormPdf() {
  const doc = await buildPartFormPdf();
  doc.save('ProxemicsConsulting-PartForm.pdf');
}
